/**
 * Auth Store - JWT Authentication
 * Manages user authentication state with token refresh
 * 
 * SECURITY NOTE:
 * - Access token is stored in memory only (not persisted)
 * - Refresh token is stored in httpOnly cookie by the server
 * - Only user info and isAuthenticated flag are persisted to localStorage
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type UserRole = 'ADMIN' | 'OPERATOR' | 'VIEWER';

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthActions {
  login: (user: User, accessToken: string) => void;
  logout: () => void;
  updateTokens: (accessToken: string) => void;
  setLoading: (isLoading: boolean) => void;
  setUser: (user: User) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // State
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,

      // Actions
      login: (user, accessToken) => {
        set({
          user,
          accessToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      updateTokens: (accessToken) => {
        set({ accessToken });
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setUser: (user) => {
        set({ user });
      },
    }),
    {
      name: 'gridinspect-auth',
      // SECURITY: Only persist non-sensitive data
      // Access token and refresh token are NOT persisted
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
